import { FC } from "react";
import {
  Flex,
  AvatarGroup,
  Avatar,
  Text,
  Box,
  Tag,
  useColorMode,
} from "@chakra-ui/react";
import { AiOutlineInfoCircle } from "react-icons/ai";

// Types
import { DefiItem, DefiItemActionData } from "@customtypes/global";

// Components
import { TextPrimary } from "theme";
import Actions from "./Actions";

// Images
import sdIcon from "assets/images/sd.png";

// Constants
import { defiIcons, PartnerTypes } from "constants/defi";
import { defiCurrencyIconMapping } from "@constants/dappWalletMapping";

interface PropTypes {
  defiItem: DefiItem;
  links: DefiItemActionData[];
  APY?: string;
  TVL?: string;
  hasAPY?: boolean;
  isSDPool?: boolean;
  tags?: string[];
  paused?: boolean;
  itemData: any;
}

const Item: FC<PropTypes> = ({
  defiItem,
  links,
  APY,
  TVL,
  hasAPY,
  tags,
  isSDPool,
  paused,
}: PropTypes) => {
  const { colorMode } = useColorMode();

  const { type, token } = defiItem;

  const isVault = type === PartnerTypes.VAULT;

  return (
    <Flex
      justify="space-between"
      align={{ base: undefined, md: "center" }}
      flexDir={{ base: "column", md: "row" }}
      gap={{ base: "6", md: undefined }}
    >
      <Flex flex="1" gap={{ base: "0", lg: "5rem" }}>
        <Flex gap="4" align="center" flex="1">
          {type === PartnerTypes.LENDING_BORROWING ||
          (type === PartnerTypes.DEX && defiItem.showToken === false) ? (
            <Box w={{ base: "fit-content", xl: "48px" }}>
              <Avatar
                bg="backgroundSecondary"
                name="BNBx"
                size="xs"
                src={
                  defiCurrencyIconMapping[
                    process.env.NEXT_PUBLIC_XTOKEN?.toLowerCase() || ""
                  ][colorMode].src
                }
              />
            </Box>
          ) : (
            <>
              <AvatarGroup spacing="-1" size="xs">
                {!isVault && (
                  <Avatar
                    name={isSDPool ? "SD" : "BNBx"}
                    src={
                      isSDPool
                        ? sdIcon.src
                        : defiCurrencyIconMapping[
                            process.env.NEXT_PUBLIC_XTOKEN?.toLowerCase() || ""
                          ][colorMode].src
                    }
                    zIndex="1"
                    borderWidth="0"
                    bg="backgroundSecondary"
                  />
                )}
                {defiItem?.tokenIconKey && (
                  <Avatar
                    name="token"
                    src={
                      defiCurrencyIconMapping[
                        defiItem?.tokenIconKey?.toLowerCase()
                      ][colorMode].src || ""
                    }
                    borderWidth="0"
                  />
                )}
              </AvatarGroup>
            </>
          )}
          <Box>
            {isVault ? (
              <TextPrimary fontSize="1rem" fontWeight="600">
                {token}
              </TextPrimary>
            ) : (
              <TextPrimary fontSize="1rem" fontWeight="600">
                {`${isSDPool ? "SD" : "BNBx"}${
                  defiItem.showToken !== false ? ` / ${token}` : ""
                }`}
              </TextPrimary>
            )}
            {tags?.map((tag) => (
              <Tag
                key={tag}
                size="sm"
                bg={tag === "NEW" ? "#EBF8FF" : "#F0FFF4"}
                color={tag === "NEW" ? "#3182CE" : "#38A169"}
                mt="1"
                fontWeight="700"
                fontSize="10px"
              >
                {tag}
              </Tag>
            ))}
          </Box>
        </Flex>

        <Flex align="center" display={{ base: "inherit", lg: "none" }} gap={2}>
          <AvatarGroup spacing="0.5" size="xs">
            {defiItem.pairPlatform && (
              <Avatar
                border="none"
                bg="transparent"
                name={defiItem.pairPlatform.title}
                src={defiIcons[defiItem?.pairPlatform?.icon_key][colorMode].src}
              />
            )}
            <Avatar
              name="platform"
              src={defiIcons[defiItem?.platform?.icon_key][colorMode].src}
              bg="transparent"
              border="none"
            />
          </AvatarGroup>
        </Flex>
      </Flex>

      <Flex
        flex="1"
        justify={{ base: "space-between", md: "flex-start" }}
        gap="16"
        align="center"
        opacity={type === PartnerTypes.OPTION_TADING ? 0 : 1}
        display={
          type === PartnerTypes.OPTION_TADING
            ? { base: "none", md: "inherit" }
            : { base: "inherit" }
        }
      >
        <TextPrimary fontSize="1rem" fontWeight="600" width="130px">
          {hasAPY ? "APY:" : "APR:"}
          <Flex align="center" display="inline-flex">
            <Text ml=".5rem">{APY ? `${APY} %` : "-"}</Text>
            {defiItem.apyMessage && (
              <Text ml=".5rem" color="textSecondary" fontSize="14px">
                <AiOutlineInfoCircle />
              </Text>
            )}
          </Flex>
        </TextPrimary>

        <TextPrimary fontSize="1rem" fontWeight="600">
          {type === PartnerTypes.LEVERAGE_TADING ? "VOL:" : "TVL:"}
          <Text as="span" ml=".5rem">
            {TVL || "-"}
          </Text>
        </TextPrimary>
      </Flex>

      <Actions
        id={defiItem.apiKey}
        type={type}
        links={links}
        isPaused={paused as boolean}
      />
    </Flex>
  );
};

export default Item;
